/** Lightweight inline-SVG charts for the analytics view — no charting
 * dependency. An equity curve (cumulative P&L line) and a per-period P&L
 * bar chart. Both scale to a fixed viewBox and stretch responsively. */

import { useState, type MouseEvent, type ReactElement } from "react";
import type { TimeseriesBucket } from "@/lib/reportsApi";
import {
  boundsWithZero,
  cumulative,
  xScale,
  yScale,
  type Bounds,
  type CumPoint,
} from "./analyticsMath";

const W = 640;
const H = 180;

export function EquityCurve({
  buckets,
}: Readonly<{ buckets: readonly TimeseriesBucket[] }>): ReactElement {
  const points = cumulative(buckets);
  const bounds = boundsWithZero(points.map((p) => p.cum));
  const [hover, setHover] = useState<number | null>(null);
  const path = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${xScale(i, points.length, W).toFixed(1)} ${yScale(p.cum, bounds, H).toFixed(1)}`,
    )
    .join(" ");
  const ending = points.at(-1)?.cum ?? 0;
  const stroke = ending >= 0 ? "var(--success, #16a34a)" : "var(--danger, #dc2626)";
  const solo = points.length === 1 ? points[0] : undefined;
  const onMove = (e: MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    setHover(Math.min(points.length - 1, Math.max(0, Math.round(frac * (points.length - 1)))));
  };
  const hp = hover !== null ? points[hover] : undefined;
  return (
    <div>
      <div className="mb-1 text-[11px] uppercase tracking-wider text-ink-secondary">
        Cumulative P&L (equity curve)
      </div>
      <div
        className="relative"
        onMouseMove={onMove}
        onMouseLeave={() => {
          setHover(null);
        }}
      >
        <CurveSvg bounds={bounds} stroke={stroke} path={path} solo={solo} />
        {hp !== undefined && (
          <CurveHover point={hp} bounds={bounds} frac={fracFor(hover ?? 0, points.length)} />
        )}
      </div>
    </div>
  );
}

function CurveSvg({
  bounds,
  stroke,
  path,
  solo,
}: Readonly<{
  bounds: Bounds;
  stroke: string;
  path: string;
  solo: CumPoint | undefined;
}>): ReactElement {
  return (
    <svg
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      preserveAspectRatio="none"
      className="h-40 w-full"
      role="img"
      aria-label="Cumulative P&L (equity curve)"
    >
      <ZeroLine bounds={bounds} />
      {solo !== undefined && <circle cx={0} cy={yScale(solo.cum, bounds, H)} r={3} fill={stroke} />}
      <path d={path} fill="none" stroke={stroke} strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}

function fracFor(i: number, n: number): number {
  return n <= 1 ? 0 : i / (n - 1);
}

function CurveHover({
  point,
  bounds,
  frac,
}: Readonly<{ point: CumPoint; bounds: Bounds; frac: number }>): ReactElement {
  const left = `${(frac * 100).toFixed(2)}%`;
  const top = `${((yScale(point.cum, bounds, H) / H) * 100).toFixed(2)}%`;
  return (
    <>
      <div
        className="pointer-events-none absolute top-0 bottom-0 w-px bg-line-strong"
        style={{ left }}
      />
      <div
        className="pointer-events-none absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-surface-1 bg-accent"
        style={{ left, top }}
      />
      <div
        className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-ctl border border-line bg-surface-2 px-2 py-1 text-[11px] shadow"
        style={{ left: clampLeft(frac), top }}
      >
        <div className="font-medium text-ink">{point.period}</div>
        <div className={point.cum >= 0 ? "text-success" : "text-danger"}>
          Total {signed(point.cum)}
        </div>
        <div className="text-ink-secondary">This period {signed(point.pnl)}</div>
      </div>
    </>
  );
}

function clampLeft(frac: number): string {
  return `${(Math.min(0.9, Math.max(0.1, frac)) * 100).toFixed(2)}%`;
}

function signed(v: number): string {
  const sign = v < 0 ? "-" : "+";
  return `${sign}Rs ${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
}

export function PnlBars({
  buckets,
  onSelect,
}: Readonly<{
  buckets: readonly TimeseriesBucket[];
  onSelect?: (period: string) => void;
}>): ReactElement {
  const bounds = boundsWithZero(buckets.map((b) => b.pnl));
  const zeroY = yScale(0, bounds, H);
  const slot = buckets.length > 0 ? W / buckets.length : W;
  const clickable = onSelect !== undefined;
  return (
    <ChartFrame label={clickable ? "P&L per period (click to drill in)" : "P&L per period"}>
      <ZeroLine bounds={bounds} />
      {buckets.map((b, i) => (
        <Bar
          key={b.period}
          bucket={b}
          x={i * slot}
          slot={slot}
          bounds={bounds}
          zeroY={zeroY}
          {...(onSelect !== undefined ? { onSelect } : {})}
        />
      ))}
    </ChartFrame>
  );
}

function Bar({
  bucket,
  x,
  slot,
  bounds,
  zeroY,
  onSelect,
}: Readonly<{
  bucket: TimeseriesBucket;
  x: number;
  slot: number;
  bounds: Bounds;
  zeroY: number;
  onSelect?: (period: string) => void;
}>): ReactElement {
  const y = yScale(bucket.pnl, bounds, H);
  const barW = Math.max(1, slot * 0.6);
  const fill = bucket.pnl >= 0 ? "var(--success, #16a34a)" : "var(--danger, #dc2626)";
  return (
    <g>
      {onSelect !== undefined && (
        <rect
          x={x}
          y={0}
          width={slot}
          height={H}
          fill="transparent"
          className="cursor-pointer"
          onClick={() => {
            onSelect(bucket.period);
          }}
        >
          <title>{`${bucket.period}: ${signed(bucket.pnl)} · ${String(bucket.trades)} trade(s)`}</title>
        </rect>
      )}
      <rect
        x={x + (slot - barW) / 2}
        y={Math.min(y, zeroY)}
        width={barW}
        height={Math.max(1, Math.abs(y - zeroY))}
        rx={1}
        fill={fill}
        pointerEvents="none"
      />
    </g>
  );
}

function ZeroLine({ bounds }: Readonly<{ bounds: Bounds }>): ReactElement {
  const y = yScale(0, bounds, H);
  return (
    <line
      x1={0}
      y1={y}
      x2={W}
      y2={y}
      stroke="var(--line, #e5e7eb)"
      strokeWidth={1}
      strokeDasharray="3 3"
    />
  );
}

function ChartFrame({
  label,
  children,
}: Readonly<{ label: string; children: React.ReactNode }>): ReactElement {
  return (
    <div>
      <div className="mb-1 text-[11px] uppercase tracking-wider text-ink-secondary">{label}</div>
      <svg
        viewBox={`0 0 ${String(W)} ${String(H)}`}
        preserveAspectRatio="none"
        className="h-40 w-full"
        role="img"
        aria-label={label}
      >
        {children}
      </svg>
    </div>
  );
}
